import { useMemo, useState, forwardRef, useRef, useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  Grid,
  IconButton,
  TextField,
  Icon,
  Radio,
  Checkbox,
} from '@material-ui/core'
import {
  useTable,
  useFlexLayout,
  useResizeColumns,
  usePagination,
  useSortBy,
  useGlobalFilter,
  useAsyncDebounce,
  useGroupBy,
  useExpanded,
  useRowSelect,
  useMountedLayoutEffect,
} from 'react-table'
import { withStyles, makeStyles } from '@material-ui/core/styles'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { matchSorter } from 'match-sorter' // A great library for fuzzy filtering/sorting items
import regeneratorRuntime from 'regenerator-runtime' // eslint-disable-line no-unused-vars
import { func, object, arrayOf, number, bool, any } from 'prop-types'

const StyledTableCell = withStyles((theme) => ({
  head: {
    '&:only-child': {
      backgroundColor: theme.palette.secondary.dark,
      color: theme.palette.secondary.contrastText,
    },
    whiteSpace: 'nowrap',
    padding: 5,
    borderRight: 1,
    borderRightStyle: 'solid',
    borderRightColor: theme.palette.action.hover,
  },
  body: {
    fontSize: 12,
    fontWeight: 300,
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 7,
    paddingRight: 7,
    whiteSpace: 'unset',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    transition: '0.3, ease',
    transitionproperty: 'width,min-width,padding,opacity',
  },
}))(TableCell)

const StyledSearchTableCell = withStyles((theme) => ({
  head: {
    whiteSpace: 'nowrap',
    padding: 0,
    margin: 0,
  },
}))(TableCell)

const StyledCheckbox = withStyles((theme) => ({
  root: {
    width: '18px',
    height: '18px',
    padding: 0,
    margin: 0,
  },
}))(Checkbox)

const StyledExpandMoreIcon = withStyles((theme) => ({
  root: {
    fontSize: 12,
    padding: 0,
    margin: 0,
  },
}))(ExpandMoreIcon)

const StyledExpandLessIcon = withStyles((theme) => ({
  root: {
    fontSize: 12,
    padding: 0,
    margin: 0,
  },
}))(ExpandLessIcon)

const StyledIcon = withStyles((theme) => ({
  root: {
    fontSize: 12,
    padding: 0,
    margin: 0,
  },
}))(Icon)

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      background: theme.palette.action.disabledBackground,
    },
  },
}))(TableRow)

const ColorButton = withStyles((theme) => ({
  root: {
    margin: theme.spacing(1),
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      color: theme.palette.secondary.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
}))(Button)

const StyledIconButton = withStyles((theme) => ({
  root: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.action.disabledBackground,
    '&:hover': {
      background: theme.palette.primary.main,
    },
  },
}))(IconButton)

const useStyles = makeStyles({
  table: {
    minWidth: 700,
    maxWidth: '100%',
    width: '100%',
  },
  iconPict: {
    fontSize: 18,
    padding: '0px',
    margin: '0px',
    textAlign: 'right',
    width: '100%',
  },
})

// Define a default UI for filtering
function GlobalFilter({ globalFilter, setGlobalFilter }) {
  const [value, setValue] = useState(globalFilter)
  const onChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined)
  }, 200)

  return (
    <TextField
      value={value || ''}
      onChange={(e) => {
        setValue(e.target.value)
        onChange(e.target.value)
      }}
      placeholder="Search"
      type="search"
      size="small"
    />
  )
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] })
}
// Let the table remove the filter if the string is empty
fuzzyTextFilterFn.autoRemove = (val) => !val

// IndeterminateCheckbox will draw the checkbox in the table
const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef()
  const resolvedRef = ref || defaultRef

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate
  }, [resolvedRef, indeterminate])

  return (
    <>
      <StyledCheckbox ref={resolvedRef} {...rest} />
    </>
  )
})
// IndeterminateRadioBox will draw the radio selection in the table
const IndeterminateRadioBox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef()
  const resolvedRef = ref || defaultRef

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate
  }, [resolvedRef, indeterminate])

  return (
    <>
      <Radio type="radio" size={'small'} ref={resolvedRef} {...rest} />
    </>
  )
})

export default function MyTable({
  columns,
  data,
  defaultPageSize,
  defaultPageIndex,
  defaultResponse,
  maxPageCount,
  onPageSizeChange,
  onResponseChange,
  onPageChange,
  manualPagination,
  defaultSortColumn,
  disableSort,
  groupByID,
  defaultSelectedRows,
  onRowSelectChange,
  onFlatRowSelectChange,
  useRadioSelection,
  useCheckboxSelection,
}) {
  const classes = useStyles()

  if (typeof defaultPageIndex !== 'number') {
    defaultPageIndex = 0
  }
  if (typeof defaultPageSize !== 'number') {
    defaultPageSize = 10
  }
  if (typeof manualPagination !== 'boolean') {
    manualPagination = false
  }
  if (typeof disableSort !== 'boolean') {
    disableSort = false
  }
  if (typeof useRadioSelection !== 'boolean') {
    useRadioSelection = false
  }
  if (typeof useCheckboxSelection !== 'boolean') {
    useCheckboxSelection = false
  }
  if (typeof onPageChange !== 'function') {
    onPageChange = (pageIndex) => {
      pageIndex = defaultPageIndex
      if (typeof pageIndex !== 'number') {
        pageIndex = 0
      }
    }
  }
  if (typeof onPageSizeChange !== 'function') {
    onPageSizeChange = (pageSize, pageIndex) => {
      pageSize = defaultPageSize
      pageIndex = defaultPageIndex

      if (typeof defaultPageSize !== 'number') {
        pageSize = 0
      }
      if (typeof defaultPageIndex !== 'number') {
        pageIndex = 0
      }
    }
  }
  if (typeof onResponseChange !== 'function') {
    onResponseChange = (pageSize, pageIndex, response) => {
      pageSize = defaultPageSize
      pageIndex = defaultPageIndex
      response = defaultResponse

      if (typeof defaultPageSize !== 'number') {
        pageSize = 0
      }
      if (typeof defaultPageIndex !== 'number') {
        pageIndex = 0
      }
    }
  }
  if (typeof onRowSelectChange !== 'function') {
    onRowSelectChange = (rowsSelected) => {
      rowsSelected = {}
    }
  }
  if (typeof onFlatRowSelectChange !== 'function') {
    onFlatRowSelectChange = (rowsSelected) => {
      rowsSelected = [{}]
    }
  }
  if (!Array.isArray(defaultSortColumn)) {
    defaultSortColumn = []
  } else {
    if (defaultSortColumn.length > 0) {
      if (typeof defaultSortColumn[0] !== 'object') {
        defaultSortColumn = []
      }
    }
  }
  if (!Array.isArray(groupByID)) {
    groupByID = []
  }
  if (typeof defaultSelectedRows !== 'object') {
    defaultSelectedRows = {}
  }

  const filterTypes = useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    }),
    [],
  )

  // Use the state and functions returned from useTable to build your UI
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    setGlobalFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    visibleColumns,
    selectedFlatRows,
    state: { pageIndex, pageSize, globalFilter, selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: Number(defaultPageIndex),
        pageSize: Number(defaultPageSize),
        sortBy: defaultSortColumn,
        groupBy: groupByID,
        selectedRowIds: defaultSelectedRows,
      },
      disableSortRemove: true,
      autoResetSelectedRows: false,
      manualPagination: Boolean(manualPagination),
      pageCount: Number(maxPageCount),
      filterTypes,
    },
    useFlexLayout,
    useResizeColumns,
    useGlobalFilter,
    useGroupBy,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
        // fix the parent group of the selection button to not be resizable
        const selectionGroupHeader = headerGroups[0].headers[0]
        selectionGroupHeader.canResize = false
      })
      if (useCheckboxSelection) {
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selection',
            width: 20,
            // The header can use the table's getToggleAllRowsSelectedProps method
            // to render a checkbox
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <div>
                <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
              </div>
            ),
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a checkbox
            Cell: ({ row, toggleRowSelected }) => {
              const currentState = row.getToggleRowSelectedProps()
              return <IndeterminateCheckbox {...currentState} />
            },
          },
          ...columns,
        ])
      } else if (useRadioSelection) {
        hooks.visibleColumns.push((columns) => [
          // Let's make a column for selection
          {
            id: 'selection',
            width: 20,
            // The cell can use the individual row's getToggleRowSelectedProps method
            // to the render a radio button
            Cell: ({ row, toggleAllRowsSelected, toggleRowSelected }) => {
              const currentState = row.getToggleRowSelectedProps()
              return (
                <IndeterminateRadioBox
                  {...currentState}
                  onClick={() => {
                    toggleAllRowsSelected(false)
                    var isNotChecked = !currentState.checked
                    var myID = row.id
                    toggleRowSelected(myID, isNotChecked)
                  }}
                />
              )
            },
          },
          ...columns,
        ])
      }
    },
  )

  useMountedLayoutEffect(() => {
    onRowSelectChange && onRowSelectChange(selectedRowIds)
  }, [onRowSelectChange, selectedRowIds])

  useMountedLayoutEffect(() => {
    onFlatRowSelectChange && onFlatRowSelectChange(selectedFlatRows)
  }, [onFlatRowSelectChange, selectedFlatRows])

  // Render the UI for your table
  return (
    <>
      <Table className={classes.table} {...getTableProps()}>
        <TableHead>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <StyledTableCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                >
                  {column.render('Header')}
                  <>
                    {column.isSorted && !disableSort ? (
                      column.isSortedDesc ? (
                        <StyledIconButton size={'small'}>
                          <ArrowDownwardIcon className={classes.iconPict} />
                        </StyledIconButton>
                      ) : (
                        <StyledIconButton size={'small'}>
                          <ArrowUpwardIcon className={classes.iconPict} />
                        </StyledIconButton>
                      )
                    ) : (
                      ''
                    )}
                  </>
                </StyledTableCell>
              ))}
            </TableRow>
          ))}
          <TableRow>
            <StyledSearchTableCell colSpan={visibleColumns.length}>
              <GlobalFilter
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
              />
            </StyledSearchTableCell>
          </TableRow>
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            const { key: rowKey, ...rowProps } = row.getRowProps()
            return (
              <StyledTableRow key={rowKey} {...rowProps}>
                {row.cells.map((cell) => {
                  return (
                    <StyledTableCell key={rowKey} {...cell.getCellProps()}>
                      {cell.isGrouped ? (
                        // If it's a grouped cell, add an expander and row count
                        <>
                          <StyledIcon {...row.getToggleRowExpandedProps()}>
                            {row.isExpanded ? (
                              <StyledExpandLessIcon />
                            ) : (
                              <StyledExpandMoreIcon />
                            )}
                          </StyledIcon>
                          {cell.render('Cell')} ({row.subRows.length})
                        </>
                      ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
                        // Otherwise, just render the regular cell
                        cell.render('Cell')
                      )}
                    </StyledTableCell>
                  )
                })}
              </StyledTableRow>
            )
          })}
        </TableBody>
      </Table>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <ColorButton
            onClick={() => {
              previousPage()
              onPageChange(Number(pageIndex) - 1)
            }}
            disabled={!canPreviousPage}
            size="large"
            color="primary"
          >
            Previous
          </ColorButton>
        </Grid>
        <Grid item>
          <center>
            <span>
              Page <strong>{Number(pageIndex) + 1}</strong> of{' '}
              <strong>{pageOptions.length}</strong>
            </span>
            <br />
            <Select
              value={Number(pageSize)}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                onPageSizeChange(Number(e.target.value), Number(pageIndex))
              }}
            >
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <MenuItem key={pageSize} value={pageSize}>
                  Show {pageSize}
                </MenuItem>
              ))}
            </Select>
          </center>
        </Grid>
        <Grid item>
          <ColorButton
            onClick={() => {
              nextPage()
              onPageChange(Number(pageIndex) + 1)
            }}
            disabled={!canNextPage}
            size="large"
            color="primary"
          >
            Next
          </ColorButton>
        </Grid>
      </Grid>
    </>
  )
}

MyTable.propTypes = {
  columns: arrayOf(object),
  data: arrayOf(object),
  defaultPageSize: number,
  defaultPageIndex: number,
  maxPageCount: number,
  onPageSizeChange: func,
  onResponseChange: func,
  onPageChange: func,
  manualPagination: bool,
  defaultSortColumn: arrayOf(any),
  disableSort: bool,
  groupByID: arrayOf(any),
  defaultSelectedRows: object,
  onRowSelectChange: func,
  onFlatRowSelectChange: func,
  useRadioSelection: bool,
  useCheckboxSelection: bool,
}
